import React, {
    useEffect,
    useRef,
    useState
} from 'react';

import {
    AjaxListingContext,
    AjaxListingNextButton
} from '$partials/base/ajax-listing';

import {
    Footer,
    Header
} from '$partials/global';

import {
    useMultiSelectFilter,
    useSingleSelectFilter
} from '$hooks/filters';

import {
    IconCheck,
    IconClose,
    IconFilter
} from '$partials/icons';

import { EventListingItem } from '$partials/items';
import { useAjaxListing } from '$hooks/ajax-listing';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').EventListingPage}
 */
const EventListingPage = (props) => {
    // page
    const page = props?.data?.page;

    // use ajax
    const isPastFilter = useSingleSelectFilter(page?.filters?.isPast);
    const generalPublicOnlyFilter = useSingleSelectFilter(page?.filters?.generalPublicOnly);
    const eventLocationFilter = useMultiSelectFilter(page?.filters?.eventLocation);
    const eventTypeFilter = useMultiSelectFilter(page?.filters?.eventType);
    const whenFilter = useSingleSelectFilter(page?.filters?.when);

    const listing = useAjaxListing({
        infinite: true,
        items: page?.items?.edges,
        page: page?.items?.pageInfo?.page,
        path: `/api/v1/listings${page?.url ?? ''}`,
        totalItems: page?.items?.total,
        totalPages: page?.items?.pageInfo?.totalPages,
        query: {
            isPast: isPastFilter.value || undefined,
            eventType: eventTypeFilter.value || undefined,
            eventLocation: eventLocationFilter.value || undefined,
            generalPublicOnly: generalPublicOnlyFilter.value || undefined,
            when: whenFilter.value || undefined
        }
    });

    // references
    const currentInnerWidth = useRef(0);

    // state
    const [isSmallScreen, setIsSmallScreen] = useState(true);
    const [showFilters, setShowFilters] = useState(false);

    // methods
    const handleClearFiltersClick = () => {
        eventLocationFilter.setValue([]);
        eventTypeFilter.setValue([]);
        isPastFilter.setValue(undefined);
        generalPublicOnlyFilter.setValue(undefined);
        whenFilter.setValue(undefined);
    };

    const handleResize = () => {
        if (currentInnerWidth.current !== window.innerWidth) {
            currentInnerWidth.current = window.innerWidth;

            if (window.innerWidth < 992) {
                if (!isSmallScreen) {
                    setIsSmallScreen(true);
                }

                if (showFilters) {
                    setShowFilters(false);
                }
            } else {
                if (isSmallScreen) {
                    setIsSmallScreen(false);
                }

                if (!showFilters) {
                    setShowFilters(true);
                }
            }
        }
    };

    const handleSetShowFilters = () => {
        setShowFilters(!showFilters);
    };

    // hooks
    useEffect(() => {
        window.addEventListener('resize', handleResize, { passive: true });
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, [
        isSmallScreen,
        setIsSmallScreen,
        setShowFilters,
        showFilters
    ]);

    // misc
    const appliedFiltersCount = 0
        + eventLocationFilter.value?.length
        + eventTypeFilter.value?.length
        + (whenFilter.value ? 1 : 0)
        + (isPastFilter.value ? 1 : 0)
        + (generalPublicOnlyFilter.value ? 1 : 0);

    // render
    return (
        <>
            <Header
                {...props?.data?.header}
                pageType={page.__typename} />

            <AjaxListingContext.Provider value={listing}>
                <main id='main-content'>
                    <div className='layout-container layout-site-outer-padding pb-8 md:pb-16'>
                        <div className='col-wrapper-breakout bg-surface-panel-yellow rounded-lg pt-24 pb-8 flex flex-wrap'>
                            {/* Heading */}
                            <section className='w-full flex flex-col sm:flex-row sm:justify-between sm:items-center gap-24 sm:gap-0 pb-panel-gap-large'>
                                <div className='flex flex-col gap-8 col-padding'>
                                    <h1 className='type-style-sans-display'>
                                        {page.title}
                                    </h1>

                                    {page?.standfirst && (
                                        <div
                                            className='rich-text type-style-sans-h3'
                                            dangerouslySetInnerHTML={{ __html: page.standfirst }} />
                                    )}
                                </div>

                                {isSmallScreen && (
                                    <div className='flex items-center gap-8 col-padding'>
                                        {/* Clear Filters */}
                                        {(appliedFiltersCount > 0) && (
                                            <button
                                                aria-label='Clear all event filters'
                                                className='type-style-sans-body text-left order-2 sm:order-1'
                                                onClick={handleClearFiltersClick}
                                                type='button'>
                                                <span className='underline'>
                                                    Clear All
                                                </span>
                                            </button>
                                        )}

                                        {/* Open Filters - Mobile/Tablet Only*/}
                                        <button
                                            aria-label={showFilters ? 'Hide event filters' : 'Show event filters'}
                                            className='button-outcome order-1 sm:order-2'
                                            onClick={handleSetShowFilters}
                                            type='button'>
                                            <span>
                                                Filters
                                            </span>

                                            {appliedFiltersCount > 0 && (
                                                <span>({appliedFiltersCount})</span>
                                            )}

                                            {(showFilters ? <IconClose /> : <IconFilter />)}
                                        </button>
                                    </div>
                                )}
                            </section>

                            {/* Filters */}
                            <aside
                                className={`w-full md:w-4/12 col-padding pb-40${showFilters ? ' block' : ' hidden'}`}
                                inert={!showFilters}>
                                <h2 className='sr-only'>
                                    Event filters
                                </h2>

                                <form
                                    action=''
                                    className='sticky top-16 flex flex-col gap-24'>
                                    {/* Type */}
                                    {eventTypeFilter.options.length > 1 && (
                                        <fieldset className='flex flex-wrap gap-8'>
                                            <legend className='w-full type-style-sans-body pb-8'>
                                                <span>
                                                    Event Type
                                                </span>

                                                <span className='sr-only'>
                                                    Filters
                                                </span>
                                            </legend>

                                            {eventTypeFilter.options.map((opt, idx) => {
                                                return (
                                                    <div key={idx}>
                                                        <input
                                                            checked={eventTypeFilter.value?.includes(opt.slug) || false}
                                                            className='sr-only peer'
                                                            id={`frm-eventType-${opt.slug}`}
                                                            name='eventType'
                                                            onChange={(e) => eventTypeFilter.toggleValue(e.target.value)}
                                                            type='checkbox'
                                                            value={opt.slug} />

                                                        <label
                                                            className='button-outcome'
                                                            htmlFor={`frm-eventType-${opt.slug}`}>
                                                            <span>{opt.title}</span>

                                                            {eventTypeFilter.value?.includes(opt.slug) ? <IconCheck /> : null}
                                                        </label>
                                                    </div>
                                                );
                                            })}
                                        </fieldset>
                                    )}

                                    {/* Location */}
                                    {eventLocationFilter.options.length > 1 && (
                                        <fieldset className='flex flex-wrap gap-8'>
                                            <legend className='w-full type-style-sans-body pb-8'>
                                                <span>
                                                    Location
                                                </span>

                                                <span className='sr-only'>
                                                    Filters
                                                </span>
                                            </legend>

                                            {eventLocationFilter.options.map((opt, idx) => {
                                                return (
                                                    <div key={idx}>
                                                        <input
                                                            checked={eventLocationFilter.value?.includes(opt.slug) || false}
                                                            className='sr-only peer'
                                                            id={`frm-eventType-${opt.slug}`}
                                                            name='eventType'
                                                            onChange={(e) => eventLocationFilter.toggleValue(e.target.value)}
                                                            type='checkbox'
                                                            value={opt.slug} />

                                                        <label
                                                            className='button-outcome'
                                                            htmlFor={`frm-eventType-${opt.slug}`}>
                                                            <span>{opt.title}</span>

                                                            {eventLocationFilter.value?.includes(opt.slug) ? <IconCheck /> : null}
                                                        </label>
                                                    </div>
                                                );
                                            })}
                                        </fieldset>
                                    )}

                                    {/* When */}
                                    {(whenFilter.options.length > 1) && (
                                        <fieldset className='flex flex-wrap gap-8'>
                                            <legend className='w-full type-style-sans-body pb-8'>
                                                <span>
                                                    When
                                                </span>

                                                <span className='sr-only'>
                                                    Filters
                                                </span>
                                            </legend>

                                            {whenFilter.options.map((opt, idx) => {
                                                return (
                                                    <div key={idx}>
                                                        <input
                                                            checked={whenFilter.value === opt.value || false}
                                                            className='sr-only peer'
                                                            disabled={isPastFilter.value === 'yes'}
                                                            id={`frm-eventType-${opt.value}`}
                                                            name='eventType'
                                                            onChange={() => whenFilter.setValue(whenFilter.value === opt.value ? null : opt.value)}
                                                            type='checkbox'
                                                            value={opt.value} />

                                                        <label
                                                            className='button-outcome'
                                                            htmlFor={`frm-eventType-${opt.value}`}>
                                                            <span>{opt.label}</span>

                                                            {whenFilter.value === opt.value ? <IconCheck /> : null}
                                                        </label>
                                                    </div>
                                                );
                                            })}
                                        </fieldset>
                                    )}

                                    {/* Misc */}
                                    <fieldset className='flex flex-col gap-12'>
                                        <legend className='w-full type-style-sans-body pb-8'>
                                            <span className='sr-only'>
                                                Misc Filters
                                            </span>
                                        </legend>

                                        <div className='flex items-center gap-8'>
                                            <input
                                                checked={isPastFilter.value === 'yes' || false}
                                                className='checkbox'
                                                disabled={Boolean(whenFilter.value)}
                                                id='frm-isPast-yes'
                                                name='isPast'
                                                onChange={() => isPastFilter.setValue(isPastFilter.value === 'yes' ? null : 'yes')}
                                                type='checkbox'
                                                value='yes' />

                                            <label
                                                className=''
                                                htmlFor='frm-isPast-yes'>
                                                <span>Past Events</span>
                                            </label>
                                        </div>

                                        <div className='flex items-center gap-8'>
                                            <input
                                                checked={generalPublicOnlyFilter.value === 'yes' || false}
                                                className='checkbox'
                                                id='frm-generalPublicOnly-yes'
                                                name='generalPublicOnly'
                                                onChange={() => generalPublicOnlyFilter.setValue(generalPublicOnlyFilter.value === 'yes' ? null : 'yes')}
                                                type='checkbox'
                                                value='yes' />

                                            <label
                                                className=''
                                                htmlFor='frm-generalPublicOnly-yes'>
                                                <span>General Public Only</span>
                                            </label>
                                        </div>
                                    </fieldset>

                                    {/* Clear Filters */}
                                    {(appliedFiltersCount > 0) && (
                                        <button
                                            aria-label='Clear all event filters'
                                            className='hidden md:inline-block type-style-sans-body text-left'
                                            onClick={handleClearFiltersClick}
                                            type='button'>
                                            <span className='underline'>
                                                Clear All
                                            </span>
                                        </button>
                                    )}
                                </form>
                            </aside>

                            {/* Listing */}
                            <section
                                aria-busy={listing?.loading}
                                aria-label='Event listing'
                                className='w-full md:w-8/12 col-padding'
                                role='feed'>
                                <h2 className='sr-only'>
                                    Events feed
                                </h2>

                                <div className='flex flex-col gap-16'>
                                    {page?.featuredEvent && (
                                        <article
                                            aria-labelledby={page.featuredEvent.title}
                                            aria-posinset={0}
                                            aria-setsize={listing.items.length + 1}
                                            className=''
                                            key={page.featuredEvent.id}>
                                            <EventListingItem {...page.featuredEvent} />
                                        </article>
                                    )}

                                    {listing?.items?.map((itm, idx) => {
                                        return (
                                            <article
                                                aria-labelledby={itm.node.title}
                                                aria-posinset={idx + 1}
                                                aria-setsize={listing.items.length}
                                                className=''
                                                key={itm.node.id + '-' + idx}>
                                                <EventListingItem {...itm.node} />
                                            </article>
                                        );
                                    })}
                                </div>

                                {(listing?.items?.length > 0) ? (
                                    listing?.page !== listing?.totalPages && (
                                        <AjaxListingNextButton className='button-outcome mt-40'>
                                            <span>Load more&hellip;</span>
                                        </AjaxListingNextButton>
                                    )
                                ) : (
                                    !page?.featuredEvent && (
                                        <p className='type-style-sans-body'>
                                            No results

                                            <span className='sr-only'>
                                                Found
                                            </span>
                                        </p>
                                    )
                                )}
                            </section>
                        </div>
                    </div>
                </main>
            </AjaxListingContext.Provider>

            <Footer
                {...props?.data?.footer}
                exploreHide={true}
                magazineHide={true}
                pageType={page.__typename}  />
        </>
    );
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export default EventListingPage;
